// Overview tab — severity stats, assets, review progress, latest findings.

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Loading } from "@/components/ui/Loading";
import { SEVERITY_META, SeverityBadge } from "@/components/ui/SeverityBadge";
import { Stat } from "@/components/ui/Stat";
import { useDict, fmt } from "@/i18n/LocaleProvider";
import {
  useEngagementAssets,
  useEngagementFindings,
  useEngagementFindingStats,
} from "@/hooks/useEngagementDetail";

interface OverviewTabProps {
  engagementId: string;
  onGoToScan: () => void;
}

const SEVERITY_KEYS = ["critical", "high", "medium", "low", "informational"] as const;

export function OverviewTab({ engagementId, onGoToScan }: OverviewTabProps) {
  const d = useDict();
  const t = d.app.overview;
  const sev = d.app.severities;
  const assets = useEngagementAssets(engagementId);
  const findings = useEngagementFindings(engagementId);
  const stats = useEngagementFindingStats(engagementId);

  if (assets.isLoading || findings.isLoading || stats.isLoading) {
    return <Loading text={t.loading} />;
  }

  const assetList = assets.data ?? [];
  const findingList = findings.data ?? [];
  const bySeverity = stats.data?.by_severity ?? {};

  return (
    <div className="flex flex-col gap-4">
      {/* Severity stats — 2-up phones, 3-up small tablets, 5-up md+ */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {SEVERITY_KEYS.map((key) => {
          const value = bySeverity[key] ?? 0;
          const meta = SEVERITY_META[key] ?? SEVERITY_META.informational;
          return (
            <Card key={key}>
              <Stat
                label={sev[key]}
                value={value}
                color={value > 0 ? meta.c : "rgb(var(--color-dim))"}
              />
            </Card>
          );
        })}
      </div>

      {/* Assets + Review progress — stacked on mobile, 2-up from md+ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.05em] mb-3">
            {fmt(t.assets, { n: assetList.length })}
          </div>
          {assetList.map((a) => (
            <div
              key={a.id}
              className="font-mono text-[12px] text-content py-1 border-b border-stroke"
            >
              <Badge color="rgb(var(--color-accent))" bg="rgb(var(--color-accent-bg))">
                {a.asset_type}
              </Badge>
              <span className="ml-2">{a.value}</span>
            </div>
          ))}
        </Card>
        <Card>
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.05em] mb-3">
            {t.reviewProgress}
          </div>
          {[
            { label: t.needsReview, value: stats.data?.needs_review ?? 0, color: "rgb(var(--color-warning))" },
            { label: t.confirmed, value: stats.data?.confirmed ?? 0, color: "rgb(var(--color-danger))" },
            { label: t.falsePositive, value: stats.data?.false_positive ?? 0, color: "rgb(var(--color-muted))" },
            { label: t.needsRetest, value: stats.data?.needs_retest ?? 0, color: "rgb(var(--color-brand))" },
          ].map((s) => (
            <div
              key={s.label}
              className="flex justify-between font-mono text-[12px] mb-1"
            >
              <span className="text-muted">{s.label}</span>
              <span
                className="font-semibold"
                style={{ color: s.value > 0 ? s.color : "rgb(var(--color-dim))" }}
              >
                {s.value}
              </span>
            </div>
          ))}
          <div className="mt-3">
            <Button variant="primary" small onClick={onGoToScan}>
              {t.runScan}
            </Button>
          </div>
        </Card>
      </div>

      {/* Latest findings */}
      {findingList.length > 0 && (
        <Card>
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.05em] mb-3">
            {t.latestFindings}
          </div>
          {findingList.slice(0, 5).map((f) => (
            <div
              key={f.id}
              className="flex items-center gap-2 py-[6px] border-b border-stroke"
            >
              <SeverityBadge
                severity={
                  (f.severity_final || f.severity_suggested || "informational") as string
                }
              />
              <span className="font-mono text-[12px] text-content flex-1">
                {f.title}
              </span>
              {f.ai_enriched && (
                <Badge color="rgb(var(--color-accent-bright))" bg="rgb(var(--color-accent-bg))">
                  AI
                </Badge>
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
